import { useState, useEffect } from 'react';
import { constantsAPI } from '@/services/api';

/**
 * Hook pour récupérer les constantes depuis le backend
 * Évite la duplication de code et les problèmes de synchronisation
 */
const useConstants = () => {
  const [constants, setConstants] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchConstants = async () => {
      try {
        setLoading(true);
        const response = await constantsAPI.getAll();
        setConstants(response.data);
        setError(null);
      } catch (err) {
        console.error('Erreur lors du chargement des constantes:', err);
        setError(err);
        // En cas d'erreur, utiliser des constantes de fallback
        setConstants(getFallbackConstants());
      } finally {
        setLoading(false);
      }
    };

    fetchConstants();
  }, []);

  return { constants, loading, error };
};

/**
 * Constantes de fallback en cas d'erreur de chargement
 * Permet à l'application de continuer à fonctionner même si l'API est indisponible
 */
const getFallbackConstants = () => ({
  user_roles: [
    ['CLIENT', 'Client'],
    ['TRANSPORTEUR', 'Transporteur'],
    ['ADMIN', 'Admin'],
    ['AGENT', 'Agent']
  ],
  user_status: [
    ['PENDING', 'Pending'],
    ['ACTIVE', 'Active'],
    ['SUSPENDED', 'Suspended'],
    ['BANNED', 'Banned']
  ],
  transport_domains: [
    ['HEAVY_GOODS', 'Transport de marchandises lourdes'],
    ['LIGHT_GOODS', 'Transport de colis légers'],
    ['PASSENGERS', 'Transport de personnes'],
    ['COURIER', 'Service de messagerie/express'],
    ['REFRIGERATED', 'Transport frigorifique'],
    ['DANGEROUS_GOODS', 'Transport de matières dangereuses'],
    ['CONSTRUCTION', 'Transport de matériaux de construction'],
    ['AGRICULTURE', 'Transport agricole'],
    ['MINING', 'Transport minier'],
    ['FUEL', 'Transport de carburant'],
    ['WASTE', 'Transport de déchets'],
    ['FURNITURE', 'Déménagement/Transport de meubles']
  ],
  client_types: [
    ['INDIVIDUAL', 'Individual'],
    ['BUSINESS', 'Business'],
    ['NGO', 'NGO'],
    ['MINING', 'Mining']
  ],
  verification_status: [
    ['UNVERIFIED', 'Unverified'],
    ['PENDING', 'Pending'],
    ['VERIFIED', 'Verified'],
    ['REJECTED', 'Rejected']
  ]
});

/**
 * Utilitaires pour travailler avec les constantes
 */
export const constantsUtils = {
  /**
   * Trouve une constante par sa valeur
   * @param {Array} choices - Array de choix [value, label]
   * @param {string} value - Valeur à chercher
   * @returns {string} Label correspondant
   */
  getLabel: (choices, value) => {
    const choice = choices?.find(([val]) => val === value);
    return choice ? choice[1] : value;
  },

  /**
   * Trouve une constante par son label
   * @param {Array} choices - Array de choix [value, label]
   * @param {string} label - Label à chercher
   * @returns {string} Valeur correspondante
   */
  getValue: (choices, label) => {
    const choice = choices?.find(([, lab]) => lab === label);
    return choice ? choice[0] : label;
  },

  /**
   * Convertit les choix en format pour les composants Select
   * @param {Array} choices - Array de choix [value, label]
   * @returns {Array} Array d'objets {value, label}
   */
  toSelectOptions: (choices) => {
    return choices?.map(([value, label]) => ({ value, label })) || [];
  }
};

export default useConstants;
